import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BananaApi } from "../../api/BananaApi";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { useAppData } from "../../context/AppContext";
import { RelayMetrics } from "../../relay/RelayTypes";
import { SettingsApi } from "../../api/SettingsApi";
import { auth } from "../../config/firebase";
import { Link } from "react-router-dom";

type Period = 0 | 1 | 7 | 30;

const number = new Intl.NumberFormat("en-US");
const value = (amount: number | null, suffix = "") => amount == null ? "—" : `${number.format(amount)}${suffix}`;

const RelayDashboardPage = () => {
  const { data, setData } = useAppData();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>(7);
  const [metrics, setMetrics] = useState<RelayMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingRelay, setUpdatingRelay] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMetrics(await BananaApi.getRelayMetrics(period));
    } catch (error: any) {
      toast.error(error.message || "Could not load relay metrics.");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (!data.loaded) return;
    if (data.user?.role !== "admin") {
      toast.error("Administrator access required.");
      navigate("/banana-stand", { replace: true });
      return;
    }
    load();
  }, [data.loaded, data.user?.role, load, navigate]);

  const funnel = metrics ? [
    ["Created", metrics.totals.bananasCreated],
    ["Opened", metrics.totals.bananasOpened],
    ["Reacted", metrics.totals.reactions],
    ["Relayed", metrics.totals.relayChildrenCreated],
  ] as const : [];
  const funnelMax = Math.max(1, ...funnel.map(([, count]) => count));

  const toggleRelay = async () => {
    if (!metrics || updatingRelay) return;
    const enabled = !metrics.health.relayEnabled;
    if (!enabled && !window.confirm("Pause all new root and child relay creation? Existing bananas will still open.")) return;
    setUpdatingRelay(true);
    try {
      const settings = await SettingsApi.setRelayState(enabled);
      setData((current) => ({ ...current, settings: { ...current.settings, ...settings } }));
      setMetrics((current) => current ? {
        ...current,
        health: { ...current.health, relayEnabled: enabled },
      } : current);
      toast.success(enabled ? "Banana Relay is live." : "Banana Relay is paused.");
    } catch (error: any) {
      toast.error(error.message || "Could not update Banana Relay.");
    } finally {
      setUpdatingRelay(false);
    }
  };

  return <>
    <title>Relay Metrics | Tuttobanana</title>
    <main className="min-h-screen bg-gray-100 px-4 py-10 text-gray-900 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.24em] text-yellow-600">Private admin dashboard</p>
            <h1 className="mt-2 text-4xl font-black tracking-tight sm:text-5xl">Relay Metrics</h1>
            <p className="mt-3 max-w-2xl text-gray-600">The truth behind the journey—aggregate behavior only, with no names or private messages.</p>
          </div>
          <div className="flex flex-wrap gap-2" aria-label="Reporting period">
            {([[1, "24 hours"], [7, "7 days"], [30, "30 days"], [0, "All time"]] as Array<[Period, string]>).map(([days, label]) =>
              <button key={days} onClick={() => setPeriod(days)} className={`rounded-full px-4 py-2 text-sm font-bold ${period === days ? "bg-gray-900 text-white" : "bg-white text-gray-700 shadow-sm"}`}>
                {label}
              </button>)}
          </div>
        </div>

        {auth.currentUser?.isAnonymous && <div className="mb-5 flex flex-col justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center">
          <div><p className="font-black text-amber-950">Protect your admin access</p><p className="text-sm text-amber-900">Link this admin identity to Google or email so it works after clearing browser data or changing devices.</p></div>
          <Link to="/log-in" className="shrink-0 rounded-xl bg-gray-900 px-4 py-2 text-center text-sm font-bold text-white">Secure access</Link>
        </div>}

        {loading || !metrics ? <div className="rounded-3xl bg-white py-24"><LoadingSpinner label="Counting bananas..." size="md" /></div> : <>
          {metrics.truncated && <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm font-semibold text-amber-900">This period contains more than 10,000 records. Narrow the date range for complete totals.</div>}

          <section className="mb-5 rounded-3xl bg-white p-5 shadow-sm">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-gray-500">Release control</p>
                <h2 className="mt-1 text-xl font-black">Banana Relay is {metrics.health.relayEnabled ? "live" : "paused"}</h2>
                <p className="mt-1 text-sm text-gray-600">Pausing blocks new roots and children, while existing bananas remain available.</p>
              </div>
              <button onClick={toggleRelay} disabled={updatingRelay} className={`rounded-xl px-5 py-3 font-black text-white disabled:opacity-50 ${metrics.health.relayEnabled ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}>
                {updatingRelay ? "Updating..." : metrics.health.relayEnabled ? "Pause relay" : "Resume relay"}
              </button>
            </div>
          </section>

          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              [value(metrics.totals.bananasCreated), "Bananas created"],
              [value(metrics.totals.uniqueParticipants), "Participants"],
              [value(metrics.totals.descendants), "Descendants"],
              [value(metrics.totals.countries), "Countries"],
            ].map(([metric, label]) => <div key={label} className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="text-3xl font-black sm:text-4xl">{metric}</div>
              <div className="mt-1 text-sm font-semibold text-gray-500">{label}</div>
            </div>)}
          </section>

          <section className="mt-5 grid gap-5 lg:grid-cols-5">
            <div className="rounded-3xl bg-gray-900 p-6 text-white lg:col-span-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-yellow-400">Core behavior</p>
                  <h2 className="mt-1 text-2xl font-black">Relay funnel</h2>
                </div>
                <div className="rounded-full bg-yellow-400 px-4 py-2 text-center text-gray-900">
                  <div className="text-xl font-black">{value(metrics.rates.relayRate, "%")}</div>
                  <div className="text-[10px] font-bold uppercase">Relay rate</div>
                </div>
              </div>
              <div className="mt-7 space-y-4">
                {funnel.map(([label, count]) => <div key={label}>
                  <div className="mb-1 flex justify-between text-sm font-bold"><span>{label}</span><span>{number.format(count)}</span></div>
                  <div className="h-3 overflow-hidden rounded-full bg-white/15"><div className="h-full rounded-full bg-yellow-400" style={{ width: `${Math.max(count ? 4 : 0, count / funnelMax * 100)}%` }} /></div>
                </div>)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:col-span-2">
              {[
                [value(metrics.rates.openRate, "%"), "Open rate"],
                [value(metrics.rates.reactionRate, "%"), "Reaction rate"],
                [value(metrics.rates.replyOpenRate, "%"), "Reply open rate"],
                [value(metrics.timing.medianMinutesToOpen, " min"), "Median to open"],
                [value(metrics.averages.descendantsPerRoot), "Descendants/root"],
                [value(metrics.totals.repliesSent), "Replies sent"],
              ].map(([metric, label]) => <div key={label} className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="text-2xl font-black">{metric}</div><div className="mt-1 text-xs font-bold text-gray-500">{label}</div>
              </div>)}
            </div>
          </section>

          <section className="mt-5 overflow-hidden rounded-3xl bg-white shadow-sm">
            <div className="grid lg:grid-cols-3">
              <div className="bg-yellow-400 p-6 sm:p-8">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-gray-800">Viral reproduction</p>
                <div className="mt-3 text-6xl font-black tracking-tight">{metrics.virality.bananaR.toFixed(2)}</div>
                <h2 className="mt-1 text-xl font-black">Banana R</h2>
                <p className="mt-3 text-sm font-semibold text-gray-800">Opened child bananas produced per opened banana. Above 1.00 means each generation is growing.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 p-6 lg:col-span-2 sm:p-8">
                {[
                  [metrics.virality.childrenPerOpenedBanana.toFixed(2), "Children per opened banana"],
                  [value(metrics.rates.childOpenRate, "%"), "Child open rate"],
                  [value(metrics.rates.growingRootsRate, "%"), "Growing roots"],
                  [value(metrics.timing.medianViralCycleMinutes, " min"), "Median viral cycle"],
                ].map(([metric, label]) => <div key={label} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <div className="text-2xl font-black">{metric}</div><div className="mt-1 text-xs font-bold text-gray-500">{label}</div>
                </div>)}
              </div>
            </div>
            <div className="overflow-x-auto border-t border-gray-100">
              <div className="p-6 pb-3"><p className="text-xs font-bold uppercase tracking-[0.22em] text-gray-500">Cohort progression</p><h3 className="mt-1 text-xl font-black">Generation survival</h3></div>
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr><th className="px-6 py-3">Generation</th><th>Created</th><th>Opened</th><th>Continued</th><th>Survival</th></tr></thead>
                <tbody>{metrics.virality.generationTable.map((row) => <tr key={row.generation} className="border-t border-gray-100"><td className="px-6 py-3 font-bold">{row.generation === 0 ? "Root" : `Gen ${row.generation}`}</td><td>{row.created}</td><td>{row.opened}</td><td>{row.continued}</td><td>{value(row.survivalRate, "%")}</td></tr>)}</tbody>
              </table>
            </div>
          </section>

          <section className="mt-5 grid gap-5 lg:grid-cols-3">
            <div className="rounded-3xl bg-white p-6 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-gray-500">Emotional feedback</p>
              <h2 className="mt-1 text-xl font-black">Reactions</h2>
              <div className="mt-5 space-y-3 text-lg font-bold">
                <div className="flex justify-between"><span>❤️ That meant a lot</span><span>{metrics.reactionCounts.heart}</span></div>
                <div className="flex justify-between"><span>😊 Made me smile</span><span>{metrics.reactionCounts.smile}</span></div>
                <div className="flex justify-between"><span>🍌 Perfect banana</span><span>{metrics.reactionCounts.banana}</span></div>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl bg-white shadow-sm lg:col-span-2">
              <div className="p-6 pb-3"><p className="text-xs font-bold uppercase tracking-[0.22em] text-gray-500">Daily activity</p><h2 className="mt-1 text-xl font-black">Recent movement</h2></div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr><th className="px-6 py-3">Date</th><th>Created</th><th>Opened</th><th>Reacted</th><th>Relayed</th></tr></thead>
                  <tbody>{metrics.daily.slice(-14).reverse().map((row) => <tr key={row.date} className="border-t border-gray-100"><td className="px-6 py-3 font-bold">{row.date}</td><td>{row.created}</td><td>{row.opened}</td><td>{row.reacted}</td><td>{row.relayed}</td></tr>)}</tbody>
                </table>
                {!metrics.daily.length && <p className="p-6 text-gray-500">No relay activity in this period.</p>}
              </div>
            </div>
          </section>

          <section className="mt-5 grid gap-3 sm:grid-cols-3">
            {[
              [value(metrics.health.rateLimitBlocks), "Rate-limit blocks"],
              [value(metrics.health.unopenedOlderThan24Hours), "Unopened over 24 hours"],
              [metrics.health.lastActivityAt ? new Date(metrics.health.lastActivityAt).toLocaleString() : "—", "Last relay activity"],
            ].map(([metric, label]) => <div key={label} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="text-xl font-black">{metric}</div>
              <div className="mt-1 text-xs font-bold text-gray-500">{label}</div>
            </div>)}
          </section>

          <p className="mt-5 text-center text-xs text-gray-500">Updated {new Date(metrics.generatedAt).toLocaleString()} · Counts are aggregate and privacy-safe.</p>
        </>}
      </div>
    </main>
  </>;
};

export default RelayDashboardPage;
